<template>
  <Teleport to="body">
    <div v-if="open" class="paf-schedule-overlay" :style="accentStyle" @click.self="close">
      <div class="paf-schedule-modal" role="dialog" aria-modal="true" @click.stop>
        <header class="paf-schedule-modal__header">
          <h3>{{ t('publicAgendamentoForm.scheduleModalTitle') }}</h3>
          <button type="button" class="paf-schedule-modal__close" :aria-label="t('common.close')" @click="close">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div class="paf-schedule-modal__body">
          <div class="paf-schedule-calendar">
            <div class="paf-schedule-calendar__nav">
              <button type="button" class="paf-schedule-nav-btn" @click="previousMonth">
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span class="paf-schedule-calendar__month">{{ monthLabel }}</span>
              <button type="button" class="paf-schedule-nav-btn" @click="nextMonth">
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div class="paf-schedule-weekdays">
              <span v-for="day in weekdayLabels" :key="day">{{ day }}</span>
            </div>

            <div class="paf-schedule-days">
              <button
                v-for="day in calendarDays"
                :key="day.key"
                type="button"
                class="paf-schedule-day"
                :class="{
                  'paf-schedule-day--muted': !day.isCurrentMonth,
                  'paf-schedule-day--selected': selectedDate && isSameDay(selectedDate, day.date),
                  'paf-schedule-day--past': day.isPast,
                  'paf-schedule-day--unavailable': day.isUnavailable
                }"
                :disabled="!day.isCurrentMonth || day.isPast || day.isUnavailable"
                @click="selectDate(day.date)"
              >
                {{ day.day }}
              </button>
            </div>
          </div>

          <div class="paf-schedule-times">
            <p class="paf-schedule-times__hint">
              {{ selectedDate ? t('publicAgendamentoForm.scheduleTimesFor', { date: formatSelectedDate(selectedDate) }) : t('publicAgendamentoForm.scheduleSelectDateFirst') }}
            </p>
            <div v-if="loadingSlots" class="paf-schedule-times__state">
              <div class="paf-schedule-spinner" />
              <span>{{ t('publicAgendamentoForm.scheduleLoading') }}</span>
            </div>
            <p v-else-if="slotsError" class="paf-schedule-times__error">{{ slotsError }}</p>
            <p v-else-if="selectedDate && !timeSlots.length" class="paf-schedule-times__empty">
              {{ t('publicAgendamentoForm.scheduleNoSlots') }}
            </p>
            <div v-else-if="selectedDate && timeSlots.length" class="paf-schedule-times__list">
              <button
                v-for="slot in timeSlots"
                :key="slot"
                type="button"
                class="paf-schedule-time"
                :class="{ 'paf-schedule-time--selected': selectedTime === slot }"
                @click="selectedTime = slot"
              >
                {{ slot }}
              </button>
            </div>
          </div>
        </div>

        <footer class="paf-schedule-modal__footer">
          <button type="button" class="paf-schedule-btn paf-schedule-btn--ghost" @click="close">{{ t('common.cancel') }}</button>
          <button
            type="button"
            class="paf-schedule-btn paf-schedule-btn--primary"
            :disabled="!selectedDate || !selectedTime"
            @click="confirm"
          >
            {{ t('publicAgendamentoForm.scheduleConfirm') }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import publicApi from '@/services/publicApi'
import { dayHasConfiguredSlots, jsDayToConfigKey, normalizeScheduleConfig } from '@/utils/agendamentoScheduleConfig'

const props = defineProps({
  open: { type: Boolean, default: false },
  token: { type: String, required: true },
  scheduleConfig: { type: Object, default: null },
  initialDate: { type: String, default: '' },
  initialTime: { type: String, default: '' },
  accentColor: { type: String, default: '#5865F2' }
})

const accentStyle = computed(() => ({ '--plf-accent': props.accentColor || '#5865F2' }))

const emit = defineEmits(['close', 'confirm'])

const { t, locale } = useI18n()

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

const weekdayLabels = computed(() => [
  t('publicAgendamentoForm.weekdays.sun'),
  t('publicAgendamentoForm.weekdays.mon'),
  t('publicAgendamentoForm.weekdays.tue'),
  t('publicAgendamentoForm.weekdays.wed'),
  t('publicAgendamentoForm.weekdays.thu'),
  t('publicAgendamentoForm.weekdays.fri'),
  t('publicAgendamentoForm.weekdays.sat')
])

const normalizedSchedule = computed(() => normalizeScheduleConfig(props.scheduleConfig))

const currentMonth = ref(new Date())
const selectedDate = ref(null)
const selectedTime = ref('')
const timeSlots = ref([])
const loadingSlots = ref(false)
const slotsError = ref('')

const monthLabel = computed(() =>
  currentMonth.value.toLocaleDateString(dateLocale.value, { month: 'long', year: 'numeric' })
)

const calendarDays = computed(() => {
  const year = currentMonth.value.getFullYear()
  const month = currentMonth.value.getMonth()
  const firstDay = new Date(year, month, 1)
  const startDate = new Date(firstDay)
  startDate.setDate(startDate.getDate() - firstDay.getDay())

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const days = []
  for (let i = 0; i < 42; i++) {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i)
    date.setHours(0, 0, 0, 0)
    const dayKey = jsDayToConfigKey(date)
    const hasConfig = dayHasConfiguredSlots(normalizedSchedule.value, dayKey)
    days.push({
      key: date.toISOString(),
      date: new Date(date),
      day: date.getDate(),
      isCurrentMonth: date.getMonth() === month,
      isPast: date < today,
      isUnavailable: !hasConfig
    })
  }
  return days
})

watch(() => props.open, (v) => {
  if (!v) return
  slotsError.value = ''
  timeSlots.value = []
  loadingSlots.value = false
  selectedTime.value = props.initialTime || ''
  if (props.initialDate) {
    const [y, m, d] = props.initialDate.split('-').map(Number)
    if (y && m && d) {
      selectedDate.value = new Date(y, m - 1, d)
      currentMonth.value = new Date(y, m - 1, 1)
      loadAvailability(selectedDate.value)
      return
    }
  }
  selectedDate.value = null
  currentMonth.value = new Date()
})

function isSameDay (a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
}

function formatSelectedDate (date) {
  return date.toLocaleDateString(dateLocale.value, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

function toDateString (date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

async function loadAvailability (date) {
  if (!date || !props.token) {
    timeSlots.value = []
    return
  }
  loadingSlots.value = true
  slotsError.value = ''
  timeSlots.value = []
  try {
    const dataStr = toDateString(date)
    const { data } = await publicApi.get(`/public/agendamento-forms/${props.token}/disponibilidade`, {
      params: { data: dataStr }
    })
    timeSlots.value = Array.isArray(data?.horariosLivres) ? data.horariosLivres : []
    if (selectedTime.value && !timeSlots.value.includes(selectedTime.value)) {
      selectedTime.value = ''
    }
  } catch (e) {
    slotsError.value = e.response?.status === 429
      ? t('publicAgendamentoForm.rateLimit')
      : (e.response?.data?.error || t('publicAgendamentoForm.scheduleLoadError'))
  } finally {
    loadingSlots.value = false
  }
}

function selectDate (date) {
  selectedDate.value = new Date(date)
  selectedTime.value = ''
  loadAvailability(selectedDate.value)
}

function previousMonth () {
  currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() - 1, 1)
}

function nextMonth () {
  currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + 1, 1)
}

function close () {
  emit('close')
}

function confirm () {
  if (!selectedDate.value || !selectedTime.value) return
  emit('confirm', {
    dataAgendamento: toDateString(selectedDate.value),
    horario: selectedTime.value
  })
  emit('close')
}
</script>
